import { useEffect, useMemo, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import ReservationJourney from "../components/ReservationJourney";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { normalizeOffer } from "../utils/resultsPipeline";
import { getCarOffer, registerSearch } from "../api/services/carService";
import { navigateToSearchResults } from "../utils/urlParams"; // updateSearchURL is unused now, drop it
import {
  User,
  Briefcase,
  Fuel,
  MapPin,
  Plus,
  Minus,
  Snowflake,
  Zap,
  Check,
} from "lucide-react";
import { GiCarDoor } from "react-icons/gi";
import {
  buildSpecs,
  SpecPill,
  CategoryBadge,
  IncludedFeatures,
} from "../components/CarDetails";

import {
  preparePaymentSession,
  runPaymentAndConfirm,
} from "../payment/paymentOrchestrator";
import { getFiservConfig } from "../api/services/paymentService";
import { COUNTRY_LIST } from "../utils/countries";
import {
  validateField,
  validateDriverDetails as validateDriverDetailsUtil,
  toE164,
} from "../utils/validationUtils";
import { processReservation } from "../api/services/reservationService";
import { useFormContext } from "../context/FormContext";
import { updateSearchURL } from "../utils/urlParams";
import { Info } from "lucide-react";
import { RentalConditionsModal } from "../components/RentalConditions";
import { StripePaymentForm } from "../payment/stripeProvider";
import { CancellationTermsModal } from "../components/CancellationTerms";
import SessionExpiredModal from "../components/ui/SessionExpiredModal";
import {FiservPaymentForm} from "../payment/FiservPaymentForm";
/* ---------------- HELPERS ---------------- */

const formatMoney = (v, currency) => {
  return `${currency} ${Number(v || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

// const formatDate = (d) =>
//   new Date(d).toLocaleString("en-US", {
//     weekday: "short",
//     month: "short",
//     day: "numeric",
//     hour: "2-digit",
//     minute: "2-digit",
//   });

const formatDate = (d, countryCode) => {
  if (!d) return "";

  const use12Hour = countryCode === "US" || countryCode === "CA";

  return new Date(d).toLocaleString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: use12Hour, // THIS IS THE KEY
  });
};

// Parses driverAge (from FormContext) into numeric bounds.
// Adjust this if your context stores it differently (e.g. { min, max }).
// Parses driverAge (from FormContext) into numeric bounds.
// Adjust this if your context stores it differently (e.g. { min, max }).
// The age bracket a search actually used is determined by residency country,
// not stored as a range anywhere — driverAge in context is a single number
// (the age typed into the homepage form).
const getSearchAgeBounds = (country) => {
  const cc = (country || "").toUpperCase();
  return cc === "US" || cc === "CA"
    ? { min: 26, max: 70 }
    : { min: 30, max: 65 };
};

const calculateAge = (dobString) => {
  if (!dobString) return null;
  const dob = new Date(dobString);
  if (isNaN(dob.getTime())) return null;

  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const m = today.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
  return age;
};

const FIELD_META = [
  { key: "firstName", label: "First Name", placeholder: "First Name" },
  { key: "lastName", label: "Last Name", placeholder: "Last Name" },
  { key: "email", label: "Email Address", placeholder: "Email Address" },
];

/* ======================================================= */

export default function CheckoutPage() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  const searchId = params.get("search_id");
  const car_offer_hash = params.get("carOfferHash") || params.get("offer");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedExtras, setSelectedExtras] = useState({});
  const [driver, setDriver] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    residency: "",
    dob: "",
    flightNumber: "",
  });
  const [accept_terms, setAcceptTerms] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const isReservationConfirmed = (resp) => {
    return (
      resp?.booking_status === "CONFIRMED"
      // && resp?.payment_status === "CAPTURED"
    );
  };

  const [isPaying, setIsPaying] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const paymentLockRef = useRef(false);
  const processAlreadyCalledRef = useRef(false);
  const [stripeSession, setStripeSession] = useState(null); // stores publishableKey, clientSecret, amount, currency, email
  const [fiservSession, setFiservSession] = useState(null);
  const paymentFormRef = useRef(null);
  const expiryIntervalRef = useRef(null);
  const expiryHandledRef = useRef(false);
  const [isSessionExpired, setIsSessionExpired] = useState(false);
  const [isOfferInvalid, setIsOfferInvalid] = useState(false);
  const [isRefreshingOffer, setIsRefreshingOffer] = useState(false);
  const [reservationFailed, setReservationFailed] = useState(false);
  const [reservationFailMessage, setReservationFailMessage] = useState("");
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showCancellationModal, setShowCancellationModal] = useState(false);
  const [showAgeRangeModal, setShowAgeRangeModal] = useState(false);

  /* ---------- CHECKOUT FLOW STATE ---------- */

  const navigate = useNavigate();
  const {
    driverCountry,
    setDriverCountry,
    pickup,
    dropoff,
    pickupGDSCode,
    dropOffGDSCode,
    pickupDate,
    pickupTime,
    dropoffDate,
    dropoffTime,
    sameLocation,
    driverAge,
    // ✅ Add these setters:
    setPickup,
    setDropoff,
    setPickupDate,
    setPickupTime,
    setDropoffDate,
    setDropoffTime,
    isSearchSessionExpired,
    startSearchSession,
  } = useFormContext();

  const [showResidencyModal, setShowResidencyModal] = useState(false);
  const [pendingResidency, setPendingResidency] = useState(driverCountry || "");
  const [reservationPending, setReservationPending] = useState(false);
  const [pendingDetails, setPendingDetails] = useState(null);
  // const [userIp, setUserIp] = useState(null);

  // useEffect(() => {
  //   const fetchIp = async () => {
  //     try {
  //       // Use your suggested URL with cache-busting
  //       const response = await fetch(
  //         `https://ipapi.co/json/?nocache=${Date.now()}`,
  //       );
  //       if (!response.ok) throw new Error("IP fetch failed");
  //       const data = await response.json();
  //       setUserIp("127.0.0.1");
  //     } catch (err) {
  //       console.error("Could not retrieve IP:", err);
  //       setUserIp("127.0.0.1"); // Fallback so the payload doesn't break
  //     }
  //   };
  //   fetchIp();
  // }, []);

  // Sync residency from context on mount
  useEffect(() => {
    if (driverCountry) {
      setDriver((prev) => ({ ...prev, residency: driverCountry }));
    }
  }, [driverCountry]);

  // Redirect to results with same payload when currency changes
  useEffect(() => {
    const handleCurrencyChange = () => {
      const parseUrl = (raw) => {
        if (!raw) return null;
        try { return JSON.parse(raw); } catch { return raw; }
      };
      const originalSearchURL =
        parseUrl(localStorage.getItem("originalSearchURL")) ||
        parseUrl(sessionStorage.getItem("originalSearchURL"));

      if (originalSearchURL) {
        navigate(originalSearchURL, { replace: true });
      }
    };

    window.addEventListener("currencyChange", handleCurrencyChange);
    return () => window.removeEventListener("currencyChange", handleCurrencyChange);
  }, [navigate]);

  useEffect(() => {
    // When user lands on checkout, mark that they came through a search
    // so if they navigate back to home, the form restores correctly
    sessionStorage.setItem("returningFromSearch", "true");
  }, []);

 const [isConfirmingResidency, setIsConfirmingResidency] = useState(false);

const handleResidencyConfirm = async () => {
  if (!pendingResidency) return;

  setIsConfirmingResidency(true);

  try {
    const registerPayload = {
      pickup_location_id: pickupGDSCode,
      dropoff_location_id: sameLocation ? pickupGDSCode : dropOffGDSCode,
      pickup_datetime: `${pickupDate}T${pickupTime || "10:00"}:00`,
      dropoff_datetime: `${dropoffDate}T${dropoffTime || "10:00"}:00`,
      age: parseInt(driverAge, 10) || 35,
      residence: pendingResidency,
      currency: data?.currency || "USD",
    };

    const registerResponse = await registerSearch(registerPayload);
    const newSearchId = registerResponse?.search_id;

    if (!newSearchId) {
      console.error("registerSearch did not return a search_id on residency change");
      return;
    }

    setDriverCountry(pendingResidency);
    setDriver((prev) => ({ ...prev, residency: pendingResidency }));

    navigateToSearchResults(navigate, newSearchId);
  } catch (e) {
    console.error("Residency change search error:", e);
  } finally {
    setIsConfirmingResidency(false);
    setShowResidencyModal(false);
  }
};

  const getCountryName = (code) => {
    return COUNTRY_LIST.find((c) => c.code === code)?.name || code;
  };

  // Scroll the payment form into view 
  useEffect(() => {
    if ((stripeSession || fiservSession) && paymentFormRef.current) {
      paymentFormRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [stripeSession, fiservSession]);
  
  useEffect(() => {
    if (expiryIntervalRef.current) return;

    expiryIntervalRef.current = setInterval(() => {
      if (loading || isPaying) return;
      if (expiryHandledRef.current) return;
      if (!data) return; // no valid offer yet — handled separately by load()

      if (isSearchSessionExpired()) {
        expiryHandledRef.current = true;
        setIsSessionExpired(true);
      }
    }, 3000);

    return () => {
      if (expiryIntervalRef.current) {
        clearInterval(expiryIntervalRef.current);
        expiryIntervalRef.current = null;
      }
    };
  }, [loading, isPaying, data, isSearchSessionExpired]);

  // useEffect(() => {
  //   if (!isSessionExpired) return;

  //   restoreSearchResults();
  // }, [isSessionExpired]);

  const handleChooseAnotherCar = () => {
    const parseUrl = (raw) => {
      if (!raw) return null;
      try { return JSON.parse(raw); } catch { return raw; }
    };
    const originalSearchURL = parseUrl(localStorage.getItem("originalSearchURL"));

    if (originalSearchURL) {
      navigate(originalSearchURL, { replace: true });
      return;
    }
    navigate(`/results?searchId=${searchId}`, { replace: true });
  };

  /* ---------- VALIDATION STATE ---------- */

  const [validationErrors, setValidationErrors] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    dob: "",
    residency: "",
  });

  const handleDriverChange = (field, value) => {
    setDriver((prev) => ({ ...prev, [field]: value }));

    if (validationErrors[field]) {
      setValidationErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleDriverBlur = (field, value) => {
    const error = validateField(field, value);
    setValidationErrors((prev) => ({
      ...prev,
      [field]: error,
    }));
  };

  const validateDriverDetails = () => {
    const result = validateDriverDetailsUtil(driver, accept_terms);
    setValidationErrors(result.errors);

    const firstInvalid = Object.keys(result.errors).find(
      (k) => result.errors[k],
    );

    if (firstInvalid) {
      const el = document.getElementById(`driver-${firstInvalid}`);
      if (el) {
        el.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
      return false;
    }

    return true;
  };


const isDriverAgeOutOfRange = (dobValue) => {
  const bounds = getSearchAgeBounds(driverCountry);
  const dobAge = calculateAge(dobValue);
  const searchedAge = parseInt(driverAge, 10);

  if (dobAge === null) return false;

  const outOfBracket = dobAge < bounds.min || dobAge > bounds.max;
  const matchesSearchedAge = !isNaN(searchedAge) && dobAge === searchedAge;

  // Only warn if it's outside the bracket AND differs from the age
  // the user already searched with (they already acknowledged that one).
  return outOfBracket && !matchesSearchedAge;
};

  const handleBookAndPay = async () => {

    // Live re-check — the 20-min timer may not have caught expiry yet
    try {
      const check = await getCarOffer({ searchId, offerHash: car_offer_hash });
      if (!check?.is_success || !check?.results?.length) {
        setIsSessionExpired(true);
        return;
      }
    } catch (e) {
      console.error("Offer re-validation failed:", e);
      setIsSessionExpired(true);
      return;
    }

    // HARD LOCK (prevents double execution)
    if (paymentLockRef.current) return;
    if (!validateDriverDetails()) {
      paymentLockRef.current = false;
      return;
    }
    if (isDriverAgeOutOfRange(driver.dob)) {
  setShowAgeRangeModal(true);
  paymentLockRef.current = false;
  return;
}

    paymentLockRef.current = true;

    try {
      setIsPaying(true);
      setPaymentError(null);
      /**
       * Build reservation/create payload
       */
      const reservationPayload = {
        search_id: searchId,
        car_offer_hash: car_offer_hash,
        time_stamp: new Date().toISOString(),

        customer: {
          first_name: driver.firstName.trim(),
          last_name: driver.lastName.trim(),
          dob: driver.dob,
          phone: toE164(driver.phone),
          email: driver.email.trim().toLowerCase(),
          residency: driver.residency,
          flight_number: driver.flightNumber?.trim() || "",
          accept_terms,
          // ip_address: userIp,
        },

      selected_extras: Object.entries(selectedExtras)
  .filter(([_, qty]) => qty > 0)
  .map(([extraName, qty]) => {
    const extra = vehicle?.extra_info?.find((e) => e.code === extraName);
    if (!extra) {
      console.warn("[BookAndPay] no matching extra_info for code:", extraName);
    }
    return {
      code: extra?.system_code,
      vendor_code: vehicle.vendor_code,
      selected_count: qty,
    };
  }),

        additional_info: additionalInfo,
      };

      /**
       * STEP 1 — reservation/create + payment/credentials
       */
     const result = await preparePaymentSession({
  reservationPayload,
  currency,
  amount: payNow,
  email: driver.email.trim(),
  phone: driver.phone,
  paymentProvider,
});

console.log("[BookAndPay] preparePaymentSession result:", result); // TEMP LOG

if (!result.success) {
        paymentLockRef.current = false;
        setPaymentError(result.message);
        // setTimeout(() => navigate("/"), 5000);
        return;
      }

      if (result.provider === "STRIPE") {
        setStripeSession(result);
        setIsPaying(false);
        return;
      }

      // const paymentResult = await runPaymentAndConfirm(result);

      // if (!paymentResult.success) {
      //   paymentLockRef.current = false;
      //   setPaymentError("");
      //   return;
      // }

      // console.log("PROCESS INPUT", {
      //   reservationNumber: result.reservationNumber,
      //   sessionId: result.sessionId,
      // });

      // if (processAlreadyCalledRef.current) {
      //   // console.log("PROCESS already executed — skipping");
      //   return;
      // }

      // processAlreadyCalledRef.current = true;

      // const processResp = await processReservation({
      //   reservationNumber: result.reservationNumber,
      //   sessionId: paymentResult.sessionId || null,
      //   paymentIntentId: paymentResult.paymentIntentId,
      // });

      if (result.provider === "FISERV") {
        setFiservSession(result);
        setIsPaying(false);
        return;
      }
      // console.log("Process response", processResp);
      // if (!isReservationConfirmed(processResp)) {
      //   const errorCode = String(processResp?.error_code);
      //   // Logic for Pending Vendor Confirmation (6000)
      //   if (errorCode === "6000") {
      //     setPendingDetails({
      //       reservationNumber: processResp.reservation_number,
      //       message: processResp.message,
      //     });
      //     setReservationPending(true);
      //     return;
      //   }

      //   // Logic for Payment Failure (2000, 4000)
      //   if (["2000", "4000"].includes(errorCode)) {
      //     setReservationFailMessage(
      //       "We were unable to authorize your payment. Please verify your card details (number, expiry date, and CVV) and try again, or use an alternative card.",
      //     );
      //     setReservationFailed(true);

      //     // RESET LOCKS so user can fix details and click the button again
      //     paymentLockRef.current = false;
      //     processAlreadyCalledRef.current = false;
      //     setIsPaying(false);
      //     setIsPaying(false);
      //     return;
      //   }

      //   // Logic for Booking Failure (3000, 1001, 1002)
      //   if (["3000", "1001", "1002"].includes(errorCode)) {
      //     setReservationFailMessage(
      //       "This reservation could not be completed. Something went wrong during booking. Please start a new reservation.",
      //     );
      //     setReservationFailed(true);
      //     return;
      //   }

      //   // Fallback
      //   setReservationFailMessage(
      //     "We’re unable to confirm this reservation at the moment. Please try again or choose another vehicle.",
      //   );
      //   setReservationFailed(true);
      //   return;
      // }

      // navigate("/confirmation", {
      //   replace: true,
      //   state: {
      //     processResponse: processResp,
      //     reservationNumber: processResp.reservation_number,
      //     vehicle,
      //     driver,
      //     extras: selectedExtras,
      //   },
      // });

      // return; // ✅ VERY IMPORTANT

      /* ---------- NAVIGATE ONLY WHEN CONFIRMED ---------- */
   } catch (err) {
  console.error("[BookAndPay] threw:", err); // was console.error(err)
  paymentLockRef.current = false;
      processAlreadyCalledRef.current = false;
      setPaymentError(
        "We’re unable to complete your reservation at the moment. Please try again in a few minutes.",
      );

      // setTimeout(() => navigate("/"), 2500);
    } finally {
      setIsPaying(false);
      /*
       * paymentLockRef intentionally NOT reset here.
       * Resetting in finally released the lock mid-flow,
       * causing duplicate /reservation/create calls.
       * Lock resets only in catch so user can retry after failure.
       */
    }
  };
  const restoreSearchResults = () => {
    const parseUrl = (raw) => {
      if (!raw) return null;
      try {
        return JSON.parse(raw);
      } catch {
        return raw;
      }
    };

    const originalSearchURL =
      parseUrl(localStorage.getItem("originalSearchURL")) ||
      parseUrl(sessionStorage.getItem("originalSearchURL"));

    if (originalSearchURL) {
      navigate(originalSearchURL, { replace: true });
      return;
    }

    navigate(`/results?searchId=${searchId}`, { replace: true });
  };
  const handleRefreshOffer = async () => {
    setIsRefreshingOffer(true);
    setLoading(true); // ← show the Loading Trip Details screen
    try {
      const result = await getCarOffer({ searchId, offerHash: car_offer_hash });

      if (result?.is_success && result?.results?.length > 0) {
        setData(result);
        startSearchSession({ searchId });
        expiryHandledRef.current = false;
        setIsSessionExpired(false);
      } else {
        restoreSearchResults();
      }
    } catch (e) {
      console.error("Offer re-validation failed:", e);
      restoreSearchResults();
    } finally {
      setIsRefreshingOffer(false);
      setLoading(false); // ← safe even if we navigated away, since this component unmounts
    }
  };
  /* ---------------- FETCH OFFER ---------------- */

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        expiryHandledRef.current = true;
        const result = await getCarOffer({ searchId, offerHash: car_offer_hash });

        if (result?.is_success && result?.results?.length > 0) {
          expiryHandledRef.current = false;
          setData(result);
          startSearchSession({ searchId });
          return;
        }

        // Offer invalid/not found — show modal instead of silent redirect
        setIsOfferInvalid(true);
      } catch (e) {
        console.error("Offer fetch failed:", e);
        setIsOfferInvalid(true);
      } finally {
        setLoading(false);
      }
    }

    if (searchId && car_offer_hash) load();
  }, [searchId, car_offer_hash]);

  //
  // Sync context pickup/dropoff labels from offer data when it loads
  // This ensures context is never stale when residency change redirects back to results
  useEffect(() => {
    if (!data?.meta_info) return;

    // ✅ Only fill in if context is empty — never overwrite what user selected
    if (!pickup && data.meta_info.pickup_location_name) {
      setPickup(data.meta_info.pickup_location_name);
    }
    if (!dropoff && data.meta_info.dropoff_location_name) {
      setDropoff(data.meta_info.dropoff_location_name);
    }
    if (data.search_request?.pickup_datetime) {
      const [date, time] = data.search_request.pickup_datetime.split("T");
      setPickupDate(date);
      setPickupTime(time?.slice(0, 5));
    }
    if (data.search_request?.dropoff_datetime) {
      const [date, time] = data.search_request.dropoff_datetime.split("T");
      setDropoffDate(date);
      setDropoffTime(time?.slice(0, 5));
    }
  }, [data]);
  //
  /* ---------------- PRELOAD PAYMENT SDK ---------------- */

  // useEffect(() => {
  //   if (!data) return;

  //   const provider = (
  //     data?.results?.[0]?.payment_provider || "FISERV"
  //   ).toUpperCase();
  //   const offerCurrency = data?.currency || "USD";

  //   const preload = async () => {
  //     if (provider === "STRIPE") {
  //       const stripeUrl = "https://checkout.stripe.com/checkout.js";
  //       if (!document.querySelector(`script[src="${stripeUrl}"]`)) {
  //         const s = document.createElement("script");
  //         s.src = stripeUrl;
  //         s.async = true;
  //         document.body.appendChild(s);
  //       }
  //     } else {
  //       try {
  //         const config = await getFiservConfig(offerCurrency);
  //         const sdkUrl = config?.sdk_url;
  //         if (sdkUrl && !document.querySelector(`script[src="${sdkUrl}"]`)) {
  //           const s = document.createElement("script");
  //           s.src = sdkUrl;
  //           s.async = true;
  //           document.body.appendChild(s);
  //         }
  //       } catch (_) {}
  //     }
  //   };

  //   preload();
  // }, [data]);

  /* ---------------- DATA ---------------- */

  const vehicle = data?.results?.[0];
  // console.log("vehicle", vehicle);
  const isVehicleReady = !!vehicle && !!vehicle.general && !!data?.vendors;

  /* Drives gateway routing — comes from getCarOffer API */
  const paymentProvider = vehicle?.payment_provider || "FISERV";
  const additionalInfo = {
    rate_id: vehicle?.additional_info?.rate_id,
    company_number: vehicle?.additional_info?.company_number,
  };
  const normalizedVehicle = useMemo(() => {
    if (!vehicle || !data) return null;

    return normalizeOffer(vehicle, data.currency, data.vendors);
  }, [vehicle, data]);

  const vendor =
    data?.vendors?.find((v) => v.code === vehicle?.vendor_code) || {};

  const currency = data?.currency || "USD";

  /* ---------------- PRICE ---------------- */

  const rentalTotal = useMemo(() => {
    if (!vehicle) return 0;
    return (
      (vehicle.payment.total_amount || 0)
    );
  }, [vehicle]);

  const extrasTotal = useMemo(() => {
    if (!vehicle?.extra_info) return 0;

    return vehicle.extra_info.reduce((sum, extra) => {
      const qty = selectedExtras[extra.code] || 0;
      return sum + extra.customer_price * qty;
    }, 0);
  }, [selectedExtras, vehicle]);

  const payNow = vehicle?.payment?.prepaid_amount || 0;

  const payAtDesk = Number(vehicle?.payment?.pay_at_pickup || 0) + extrasTotal;

  /* ---------------- EXTRA HANDLER ---------------- */

  const changeExtra = (extra, delta) => {
    setSelectedExtras((prev) => {
      const current = prev[extra.code] || 0;
      const next = Math.max(0, Math.min(extra.max_count, current + delta));
      return { ...prev, [extra.code]: next };
    });
  };

  /* ---------------- STATES ---------------- */
  if (isOfferInvalid) {
    return (
      <SessionExpiredModal
        open={true}
        onRefresh={restoreSearchResults}
        isRefreshing={false}
        title="Session Expired"
        message="This checkout session is no longer available. You'll be taken back to search results to see more car offers."
        buttonLabel="Back to Results"
        buttonLoadingLabel="Redirecting..."
      />
    );
  }

  if (loading || !isVehicleReady) {
    return (
      <div className="flex flex-col items-center item-center py-32 gap-4 text-text-light">
        <div className="w-10 h-10 border-4 border-surface-border border-t-primary rounded-full animate-spin" />
        <span className="text-sm font-body font-medium">Loading Trip Details…</span>
      </div>
    );
  }

  // if (!vehicle)
  //   return (
  //     <div className="p-20 text-center font-semibold">Offer unavailable</div>
  //   );

  /* ======================================================= */

  const PriceSummaryCard = ({
    rentalTotal,
    extrasTotal,
    currency,
    data,
    payNow,
    payAtDesk,
  }) => {
    const overallTotal = rentalTotal + extrasTotal;

    return (
      <div className="bg-accent rounded-2xl border border-primary-soft overflow-hidden sticky top-6">
        {/* Header Block - Light tint of your brand color */}
        <div className="p-5 lg:p-6">
          <div className="flex justify-between items-center mb-1.5">
            <h5 className="text-xs uppercase tracking-wider font-heading font-bold text-text-muted">
              Total Rental price
            </h5>
            <span className="text-xs font-body font-semibold text-primary-hover bg-surface px-2.5 py-0.5 rounded-full">
              {data.rental_days} Days Rental
            </span>
          </div>
          <div className="flex justify-between items-baseline">
            <p className="text-2xl lg:text-3xl font-heading font-black text-text tracking-tight">
              {formatMoney(overallTotal, currency)}
            </p>
          </div>
        <p className="text-xs text-text-muted font-body mt-0.5">for {data.rental_days} days</p>
        </div>

        {/* Breakdown Details */}
        <div className="px-5 lg:px-6 pb-5 lg:pb-6 space-y-4">
          <div className="border-t border-primary-soft pt-4 space-y-3 text-sm font-body">
            <div className="flex justify-between">
              <span className="text-text-muted">Rental Cost</span>
              <span className="font-semibold text-text">
                {formatMoney(rentalTotal, currency)}
              </span>
            </div>

            {extrasTotal !== 0 && (
              <div className="flex justify-between">
                <span className="text-text-muted">Add-ons & Extras</span>
                <span className="font-semibold text-text">
                  {formatMoney(extrasTotal, currency)}
                </span>
              </div>
            )}
          </div>

          {/* Crisp Subtle Divider */}
          <div className="border-t border-primary-soft" />

          <div className="flex justify-between items-baseline">
            <span className="font-heading font-bold text-text">Total</span>
            <span className="font-heading font-bold text-text">
              {formatMoney(overallTotal, currency)}
            </span>
          </div>

          {/* Payment Splits */}
          <div className="space-y-2 pt-1">
            <div className="flex justify-between items-center">
            <p className="font-heading font-bold text-text">
              Pay Now
            </p>
              <p className="text-lg font-heading font-bold text-primary-hover">
                {formatMoney(payNow, currency)}
              </p>
            </div>

            {/* Pay at Counter */}
            <div className="flex justify-between items-center">
            <p className="text-text-muted font-body">
              At Rental Desk
            </p>
              <p className="text-sm font-semibold text-text">
                {formatMoney(payAtDesk, currency)}
              </p>
            </div>
          </div>

        </div>
      </div>
    );
  };

  return (
    <div className="bg-surface-raised min-h-screen pb-16 font-body">
      <div className="max-w-7xl mx-auto px-4 pt-6">
        <ReservationJourney
          currentStep={3}
          onStepClick={(stepId) => {
            if (stepId === 2) {
              handleChooseAnotherCar();
            }
          }}
        />

        <div className={`grid gap-8 mt-8 lg:grid-cols-[minmax(0,3fr)_380px] `}>
          {/* LEFT */}
          <div className="space-y-6 transition-all duration-500">
            <>
              {/* VEHICLE CARD */}
                            {/* VEHICLE CARD */}
              <div className="bg-surface rounded-2xl border border-surface-border shadow-card overflow-hidden flex flex-col md:flex-row">
                {/* Left: badge + image + supplier */}
                <div className="flex md:flex-col gap-3 p-4 md:w-52 md:shrink-0 md:border-r md:border-surface-border">
                  <div className="w-28 md:w-full shrink-0 flex flex-col items-start gap-1">
                    <CategoryBadge label={normalizedVehicle.childType} />
                    <img
                      src={normalizedVehicle.imageUrl}
                      alt={normalizedVehicle.model}
                      className="h-16 md:h-32 w-full object-contain soft-edge-mask"
                    />
                  </div>

                  {/* Mobile: title + supplier beside the image */}
                  <div className="md:hidden min-w-0 flex-1 flex flex-col justify-center">
                    <h2 className="text-[15px] font-heading font-extrabold text-text leading-tight">
                      {normalizedVehicle.model}{" "}
                      <span className="text-[11px] font-normal text-text-muted">or similar</span>
                    </h2>
                    <div className="mt-1 flex items-center gap-1.5 min-w-0">
                      {normalizedVehicle.logoUrl && (
                        <img src={normalizedVehicle.logoUrl} alt="" className="h-5 max-w-[64px] object-contain" />
                      )}
                      <span className="text-[11px] text-text-muted font-body truncate">
                        {normalizedVehicle.name}
                      </span>
                    </div>
                  </div>

                  {/* Desktop: supplier under the image */}
                  <div className="hidden md:flex flex-col items-center gap-1 mt-auto">
                    {normalizedVehicle.logoUrl && (
                      <img src={normalizedVehicle.logoUrl} alt="" className="h-10 max-w-[120px] object-contain" />
                    )}
                    <span className="text-xs font-medium text-text font-body text-center">
                      {normalizedVehicle.name}
                    </span>
                  </div>
                </div>

                {/* Right: title, specs, included features */}
                <div className="flex-1 min-w-0 px-4 pb-4 md:p-5 flex flex-col gap-4 md:justify-center">
                  <h2 className="hidden md:block text-xl font-heading font-extrabold text-text">
                    {normalizedVehicle.model}{" "}
                    <span className="text-sm font-normal text-text-muted">or similar</span>
                  </h2>

                  <div className="flex flex-wrap gap-1.5">
                    {buildSpecs(normalizedVehicle).map((s) => (
                      <SpecPill key={s.title} icon={s.icon} label={s.label} title={s.title} />
                    ))}
                  </div>

                  <div className="md:hidden">
                    <IncludedFeatures items={normalizedVehicle.inclusions} columns={1} />
                  </div>
                  <div className="hidden md:block">
                    <IncludedFeatures items={normalizedVehicle.inclusions} columns={2} />
                  </div>
                </div>
              </div>

              {/* LOCATIONS */}
              <div className="grid md:grid-cols-2 gap-6">
                {[
                  {
                    label: "Pick-up",
                    loc: vendor.pickup_location,
                    name: data.meta_info.pickup_location_name,
                    date: data.search_request.pickup_datetime,
                  },
                  {
                    label: "Drop-off",
                    loc: vendor.dropoff_location,
                    name: data.meta_info.dropoff_location_name,
                    date: data.search_request.dropoff_datetime,
                  },
                ].map((x, i) => (
                  <div key={i} className="bg-surface border border-surface-border rounded-xl p-5 shadow-card">
                    <div className="flex items-center justify-between">
                      <div className="text-xs font-body font-black text-primary uppercase tracking-wide">
                        {x.label}
                      </div>

                      {x.loc.pickup_type && (
                        <span className="text-[10px] font-body font-bold text-primary bg-primary/10 px-2 py-0.5 rounded-full uppercase tracking-wide">
                          {x.loc.pickup_type}
                        </span>
                      )}
                    </div>

                    <div className="font-heading font-bold text-text mt-1.5 text-base">
                      {formatDate(x.date, driverCountry)}
                    </div>

                    <div className="flex gap-2 mt-2 text-sm font-body font-semibold text-text">
                      <MapPin size={16} className="text-primary shrink-0 mt-0.5" /> {x.name}
                    </div>

                    <div className="text-xs font-body text-text-muted mt-1 pl-[22px]">
                      {x.loc.address}
                    </div>
                  </div>
                ))}
              </div>
              {/* EXTRAS */}
              {Array.isArray(vehicle.extra_info) &&
                vehicle.extra_info.length > 0 && (
                  <div className="bg-surface border border-surface-border rounded-xl p-6 shadow-card">
                    <h3 className="font-heading font-bold text-text mb-2 text-lg">Optional Extras</h3>

                    <p className="text-text-muted mb-4 text-sm lg:text-sm font-body">
                      You can request any of the options below. These requests
                      cannot be guaranteed and additional fees may apply.
                      Contact the rental car company to confirm availability.
                    </p>

                    {vehicle.extra_info.map((extra) => (
                      <div
                        key={extra.code}
                        className="flex justify-between items-center border border-surface-border rounded-xl py-3 px-4 mb-3 last:mb-0"
                      >
                        <div>
                          <div className="font-body font-semibold text-text">{extra.name}</div>
                          <div className="text-xs font-body text-text-muted mt-0.5">
                            {formatMoney(
                              extra.customer_price,
                              extra.customer_currency,
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          <button
                            type="button"
                            onClick={() => changeExtra(extra, -1)}
                            className="flex items-center justify-center w-8 h-8 rounded-full border border-surface-border text-text-muted hover:border-primary hover:text-primary transition-colors"
                          >
                            <Minus size={16} />
                          </button>

                          <span className="font-heading font-bold text-text w-6 text-center">
                            {selectedExtras[extra.code] || 0}
                          </span>

                          <button
                            type="button"
                            onClick={() => changeExtra(extra, 1)}
                            className="flex items-center justify-center w-8 h-8 rounded-full border border-surface-border text-text-muted hover:border-primary hover:text-primary transition-colors"
                          >
                            <Plus size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

              {/* MOBILE PRICE SECTION (Hidden on Desktop) */}
              <div className="lg:hidden">
                <PriceSummaryCard
                  rentalTotal={rentalTotal}
                  extrasTotal={extrasTotal}
                  currency={currency}
                  data={data}
                  payNow={payNow}
                  payAtDesk={payAtDesk}
                />
              </div>

              {/* DRIVER INFO */}
              <div className="bg-surface border border-surface-border rounded-xl p-6 shadow-card">
                <h3 className="font-heading font-bold text-text text-lg">Driver Details</h3>
                <p className="text-text-muted mb-4 text-sm lg:text-base font-body">
                  Ensure all details match the driver’s license and official
                  records.
                </p>

                <div className="grid md:grid-cols-2 gap-x-6 gap-y-4">
                  {FIELD_META.map((f) => (
                    <div
                      key={f.key}
                      id={`driver-${f.key}`}
                      className="flex flex-col"
                    >
                      <div
                        className={`relative w-full h-[48px] border rounded-lg transition-all flex items-center ${
                          validationErrors[f.key]
                            ? "border-destructive bg-destructive/5"
                            : "border-surface-border bg-surface focus-within:border-primary"
                        }`}
                      >
                        <input
                          placeholder={f.placeholder}
                          /* Remove font-specific classes, let the global CSS handle it */
                          className="w-full h-full px-4 bg-transparent text-[15px] font-body text-text outline-none placeholder:text-text-muted"
                          value={driver[f.key]}
                          onChange={(e) =>
                            handleDriverChange(f.key, e.target.value)
                          }
                        />
                      </div>
                      {validationErrors[f.key] && (
                        <p className="text-destructive text-[11px] font-body mt-1">
                          {validationErrors[f.key]}
                        </p>
                      )}
                    </div>
                  ))}

                  {/* Date of Birth */}
                  <div id="driver-dob" className="flex flex-col">
                    <div
                      className={`relative w-full h-[48px] border rounded-lg transition-all flex items-center ${
                        validationErrors.dob
                          ? "border-destructive bg-destructive/5"
                          : "border-surface-border bg-surface focus-within:border-primary"
                      }`}
                    >
                      <DatePicker
                        placeholder="Select Date of Birth"
                        variant="borderless"
                        className="!w-full !h-full !px-4 !flex !items-center !bg-transparent
                 [&_.ant-picker-input]:!h-full 
                 [&_.ant-picker-input>input]:!text-[15px] 
                 /* This forces the font to match First/Last name exactly */
                 [&_.ant-picker-input>input]:!font-inherit
                 [&_.ant-picker-input>input]:!font-semibold
                 [&_.ant-picker-input>input]:!leading-none 
                 /* This matches the placeholder style of your other fields */
                 [&_.ant-picker-input>input::placeholder]:!text-text-muted 
                 [&_.ant-picker-input>input::placeholder]:!font-inherit
                 [&_.ant-picker-input>input::placeholder]:!opacity-100"
                        format="YYYY-MM-DD"
                        value={driver.dob ? dayjs(driver.dob) : null}
                 onChange={(date) => {
  const value = date ? date.format("YYYY-MM-DD") : "";
  handleDriverChange("dob", value);
  handleDriverBlur("dob", value);
  if (value && isDriverAgeOutOfRange(value)) {
    setShowAgeRangeModal(true);
  }
}}
                        defaultPickerValue={dayjs().subtract(21, "year")}
                        allowClear={false}
                        disabledDate={(current) =>
                          current && current > dayjs().subtract(21, "year")
                        }
                      />
                    </div>
                    {validationErrors.dob && (
                      <p className="text-destructive text-[11px] font-body mt-1">
                        {validationErrors.dob}
                      </p>
                    )}
                  </div>

                  {/* Phone Input */}
                  <div id="driver-phone" className="flex flex-col">
                    <PhoneInput
                      country={(driverCountry || "us").toLowerCase()}
                      value={driver.phone}
                      onChange={(v) => handleDriverChange("phone", v)}
                      onBlur={() => handleDriverBlur("phone", driver.phone)}
                      containerClass="!w-full !h-[48px]"
                      inputClass={`!w-full !h-[48px] !text-[15px] !font-body !rounded-lg !border-surface-border !px-12 ${
                        validationErrors.phone
                          ? "!border-destructive !bg-destructive/5"
                          : ""
                      }`}
                      buttonClass="!rounded-l-lg !border-surface-border !bg-surface"
                    />
                    {validationErrors.phone && (
                      <p className="text-destructive text-[11px] font-body mt-1">
                        {validationErrors.phone}
                      </p>
                    )}
                  </div>

                  {/* Residency Section - Height Matched */}
                  <div className="flex flex-col" id="driver-residency">
                    <div
                      className="relative flex items-center w-full h-[48px] px-4 bg-surface border border-surface-border rounded-lg cursor-pointer hover:border-primary transition-colors group"
                      onClick={() => setShowResidencyModal(true)}
                    >
                      {/* Mini Label positioned like a floating placeholder */}
                      <span className="absolute top-1 left-4 text-[10px] font-body font-bold text-text-light uppercase tracking-tight">
                        I live in
                      </span>

                      <span className="text-text text-[15px] font-body font-semibold mt-3">
                        {getCountryName(driver.residency) || "Select Residency"}
                      </span>

                      <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
                        <span className="text-[11px] font-body font-bold text-primary hover:underline cursor-pointer">
                          (edit)
                        </span>
                        <div className="flex items-center justify-center w-4 h-4 rounded-full border border-surface-border">
                          <span className="text-[9px] font-body font-bold text-text-light italic">
                            i
                          </span>
                        </div>
                      </div>
                    </div>
                    {validationErrors.residency && (
                      <p className="text-destructive text-[11px] font-body mt-1">
                        {validationErrors.residency}
                      </p>
                    )}
                  </div>

                  <div id="driver-flightNumber" className="flex flex-col">
                    <div className="relative w-full h-[48px] border border-surface-border bg-surface rounded-lg transition-all flex items-center focus-within:border-primary">
                      <input
                        placeholder="Flight Number (Optional)"
                        className="w-full h-full px-4 bg-transparent text-[15px] font-body text-text outline-none placeholder:text-text-muted"
                        value={driver.flightNumber}
                        onChange={(e) =>
                          handleDriverChange("flightNumber", e.target.value)
                        }
                      />
                    </div>
                  </div>
                </div>
                {/* TERMS & CONDITIONS CHECKBOX */}
                <div id="driver-acceptTerms" className="mt-6">
                  <label className="flex items-start gap-3 text-xs font-body text-text-muted">
                    <input
                      type="checkbox"
                      checked={accept_terms}
                      onChange={(e) => setAcceptTerms(e.target.checked)}
                      className={`mt-0.5 h-4 w-4 rounded-full accent-primary shrink-0 ${validationErrors.accept_terms ? "outline outline-destructive" : ""}`}
                    />
                    <span>
                      I confirm that I have read, understood and agree with the{" "}
                      {/* Trigger Modal Here */}
                      <button
                        type="button"
                        onClick={() => setShowTermsModal(true)}
                        className="text-primary font-bold underline decoration-primary hover:text-primary-hover uppercase transition-colors"
                      >
                        Supplier Rental Terms
                      </button>,{" "}
                      <button
                        type="button"
                        onClick={() =>
                          window.open(
                            "/terms-and-conditions",
                            "_blank",
                            "noopener,noreferrer",
                          )
                        }
                        className="text-primary font-bold underline decoration-primary hover:text-primary-hover uppercase transition-colors"
                      >
                        Booking Terms &amp; Conditions
                      </button>{" "} and 
                      <button
                        type="button"
                        onClick={() => setShowCancellationModal(true)}
                        className="text-primary font-bold underline decoration-primary hover:text-primary-hover uppercase tracking-wide transition-colors"
                      >
                        Cancellation Terms
                      </button>{" "}
                      provided.
                    </span>
                  </label>
                  {validationErrors.acceptTerms && (
                    <p className="text-destructive text-xs font-body mt-1">
                      {validationErrors.acceptTerms}
                    </p>
                  )}
                </div>
                {/* COMPLETE BOOKING BUTTON */}
                <div ref={paymentFormRef} className="mt-6 pt-4 border-t border-surface-border">
                  {!stripeSession && !fiservSession && (
                    <button
                      onClick={handleBookAndPay}
                      disabled={isPaying}
                      className="w-full bg-primary hover:bg-primary-hover text-white font-heading font-black py-4 rounded-xl transition disabled:opacity-50"
                    >
                      {isPaying ? "Processing..." : "Continue with Payment"}
                    </button>
                  )}

                  {paymentError && (
                    <p className="text-destructive text-sm font-body mt-3 text-center">
                      {paymentError}
                    </p>
                  )}

                  {/* Inline Stripe form appears here when session is ready */}
                  {stripeSession && !isPaying && (
                    <StripePaymentForm
                      publishableKey={stripeSession.publishableKey}
                      clientSecret={stripeSession.clientSecret}
                      amount={stripeSession.amount}
                      currency={stripeSession.currency}
                      email={stripeSession.email}
                      onSuccess={async (paymentResult) => {
                        setIsPaying(true);
                        const processResp = await processReservation({
                          reservationNumber: stripeSession.reservationNumber,
                          sessionId: null,
                          stripeToken: paymentResult.stripeToken || null,
                          paymentIntentId: paymentResult.paymentIntentId,
                          customer: {
                            first_name: driver.firstName.trim(),
                            last_name: driver.lastName.trim(),
                            dob: driver.dob,
                            phone: toE164(driver.phone),
                            email: driver.email.trim().toLowerCase(),
                            residency: driver.residency,
                            flight_number: driver.flightNumber?.trim() || null,
                            accept_terms,
                            // ip_address: userIp, -> handled in website api
                          },
                        });

                        if (!isReservationConfirmed(processResp)) {
                          const errorCode = String(processResp?.error_code);
                          if (errorCode === "6000") {
                            setPendingDetails({
                              reservationNumber: processResp.reservation_number,
                              message: processResp.message,
                            });
                            setReservationPending(true);
                            setIsPaying(false);
                            return;
                          }
                          if (["2000", "4000"].includes(errorCode)) {
                            setReservationFailMessage(
                              "We were unable to authorize your payment. Please verify your card details (number, expiry date, and CVV) and try again, or use an alternative card.",
                            );
                            setReservationFailed(true);
                            setStripeSession(null);
                            paymentLockRef.current = false;
                            processAlreadyCalledRef.current = false;
                            setIsPaying(false);
                            return;
                          }
                          // Logic for Booking Failure (3000, 1001, 1002) — added for parity with main flow
                          if (["3000", "1001", "1002"].includes(errorCode)) {
                            setReservationFailMessage(
                              "This reservation could not be completed. Something went wrong during booking. Please start a new reservation.",
                            );
                            setReservationFailed(true);
                            setIsPaying(false);
                            return;
                          }
                          setReservationFailMessage(
                            "We’re unable to confirm this reservation at the moment. Please try again or choose another vehicle."
                          );
                          setReservationFailed(true);
                          setIsPaying(false);
                          return;
                        }

                        navigate("/confirmation", {
                          replace: true,
                          state: {
                            processResponse: processResp,
                            reservationNumber: processResp.reservation_number,
                            vehicle,
                            driver,
                            extras: selectedExtras,
                          },
                        });
                      }}
                      onCancel={() => {
                        setStripeSession(null);
                        paymentLockRef.current = false;
                        processAlreadyCalledRef.current = false;
                      }}
                    />
                  )}
                  {fiservSession && !isPaying && (
                    <FiservPaymentForm
                     accessToken={fiservSession.accessToken}
                      sessionId={fiservSession.sessionId}
                      config={fiservSession.config}
                      amount={payNow}
                      currency={currency}
                      onSuccess={async (paymentResult) => {
                        setIsPaying(true);
                        const processResp = await processReservation({
                          reservationNumber: fiservSession.reservationNumber,
                          sessionId: paymentResult.sessionId,
                          authenticationTransactionId: paymentResult.authenticationTransactionId,
                          customer: {
                            first_name: driver.firstName.trim(),
                            last_name: driver.lastName.trim(),
                            dob: driver.dob,
                            phone: toE164(driver.phone),
                            email: driver.email.trim().toLowerCase(),
                            residency: driver.residency,
                            flight_number: driver.flightNumber?.trim() || null,
                            accept_terms,
                            // ip_address: userIp, -> handled in website api
                          },
                        });

                        if (!isReservationConfirmed(processResp)) {
                          const errorCode = String(processResp?.error_code);

                          if (errorCode === "6000") {
                            setPendingDetails({
                              reservationNumber: processResp.reservation_number,
                              message: processResp.message,
                            });
                            setReservationPending(true);
                            setIsPaying(false);
                            return;
                          }

                          if (["2000", "4000"].includes(errorCode)) {
                            setReservationFailMessage(
                              "We were unable to authorize your payment. Please verify your card details (number, expiry date, and CVV) and try again, or use an alternative card.",
                            );
                            setReservationFailed(true);
                            setFiservSession(null);
                            paymentLockRef.current = false;
                            processAlreadyCalledRef.current = false;
                            setIsPaying(false);
                            return;
                          }

                          if (["3000", "1001", "1002"].includes(errorCode)) {
                            setReservationFailMessage(
                              "This reservation could not be completed. Something went wrong during booking. Please start a new reservation.",
                            );
                            setReservationFailed(true);
                            setIsPaying(false);
                            return;
                          }

                          setReservationFailMessage(
                            "We're unable to confirm this reservation at the moment. Please try again or choose another vehicle."
                          );
                          setReservationFailed(true);
                          setIsPaying(false);
                          return;
                        }

                        navigate("/confirmation", {
                         replace: true,
                          state: {
                            processResponse: processResp,
                            reservationNumber: processResp.reservation_number,
                            vehicle,
                            driver,
                            extras: selectedExtras,
                          },
                        });
                      }}
                      onCancel={() => {
                        setFiservSession(null);
                        paymentLockRef.current = false;
                        processAlreadyCalledRef.current = false;
                      }}
                    />
                  )}
                  {isPaying && (
                    <div className="fixed inset-0 z-[9999] bg-dark/10 backdrop-blur-sm flex flex-col items-center justify-center">
                      <div className="w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin" />

                      <span className="mt-4 text-white text-sm font-body font-medium">
                        Processing ....
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </>
            {/* PAYMENT SECTION */}
          </div>

          {/* RIGHT PRICE SIDEBAR */}

          <div className="hidden lg:block">
            <div className="sticky top-24 self-start h-fit space-y-3">
              <button
                onClick={handleChooseAnotherCar}
                className="text-sm font-body font-semibold text-text-muted hover:text-primary transition-colors"
              >
                ← Back to vehicles
              </button>
              <PriceSummaryCard
                rentalTotal={rentalTotal}
                extrasTotal={extrasTotal}
                currency={currency}
                data={data}
                payNow={payNow}
                payAtDesk={payAtDesk}
              />
            </div>
          </div>
        </div>
      </div>
      {reservationFailed && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-dark/20 backdrop-blur-sm">
          <div className="relative bg-surface rounded-2xl p-8 w-[420px] max-w-[92vw] shadow-2xl text-center border border-surface-border">
            {/* 1. Added Cross Button for Payment Errors */}
            {reservationFailMessage.includes("payment") && (
              <button
                onClick={() => setReservationFailed(false)}
                className="absolute top-4 right-4 text-text-muted hover:text-text transition-colors"
              ></button>
            )}

            <div className="bg-primary-soft w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <Info className="w-8 h-8 text-primary" />
            </div>

            <h2 className="text-xl font-heading font-bold text-text mb-3">
              {reservationFailMessage.includes("payment")
                ? "Could not Authorize your payment"
                : "Booking could not be Completed"}
            </h2>

            <p className="text-text-muted mb-6 leading-relaxed font-body">
              {reservationFailMessage}
            </p>

            {/* 2. Conditional Primary Button */}
            <button
              onClick={() => {
                if (reservationFailMessage.includes("payment")) {
                  setReservationFailed(false); // Just close to let them fix card details
                } else {
                  handleChooseAnotherCar(); // Redirect for booking failures
                }
              }}
              className="w-full bg-primary hover:bg-primary-hover text-white font-heading font-bold py-3.5 rounded-xl transition-all shadow-card"
            >
              {reservationFailMessage.includes("payment")
                ? "Try Again"
                : "Choose Another Car"}
            </button>
          </div>
        </div>
      )}
      {reservationPending && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-dark/20 backdrop-blur-sm">
          <div className="relative bg-surface rounded-2xl p-8 w-[420px] max-w-[92vw] shadow-2xl text-center border border-surface-border">
            <div className="bg-primary-soft w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <Info className="w-8 h-8 text-primary" />
            </div>

            <h2 className="text-xl font-heading font-bold text-text mb-3">
              Reservation Pending
            </h2>

            <p className="text-text-muted mb-2 leading-relaxed font-body">
              Your payment has been authorized. Your reservation is awaiting confirmation
              from the rental vendor.
            </p>

            <p className="text-text-muted mb-6 leading-relaxed font-body">
              We will update you once the vendor confirms your booking.{" "}
              {pendingDetails?.reservationNumber && (
                <>
                  <span className="font-semibold text-text">
                    Reservation Number: {pendingDetails.reservationNumber}
                  </span>
                </>
              )}
            </p>

            <button
              onClick={() => navigate("/manage-booking")}
              className="w-full bg-primary hover:bg-primary-hover text-white font-heading font-bold py-3.5 rounded-xl transition-all shadow-card"
            >
              Manage Booking
            </button>
          </div>
        </div>
      )}
     <ChangeResidencyModal
  isOpen={showResidencyModal}
  currentCountry={driver.residency}
  countries={COUNTRY_LIST}
  pendingResidency={pendingResidency}
  setPendingResidency={setPendingResidency}
  onConfirm={handleResidencyConfirm}
  isConfirming={isConfirmingResidency}
  onCancel={() => {
    setShowResidencyModal(false);
    setPendingResidency(driver.residency);
  }}
/>
<AgeRangeWarningModal
  isOpen={showAgeRangeModal}
  bounds={getSearchAgeBounds(driverCountry)}
  onUpdateSearch={() => navigate("/")}
  onClose={() => setShowAgeRangeModal(false)}
/>
      
      <RentalConditionsModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        searchId={searchId}
        offerHash={car_offer_hash}
      />
      <CancellationTermsModal
        isOpen={showCancellationModal}
        onClose={() => setShowCancellationModal(false)}
        searchId={searchId}
        carOfferHash={car_offer_hash}
      />
      <SessionExpiredModal
        open={isSessionExpired}
        onRefresh={handleRefreshOffer}
        isRefreshing={isRefreshingOffer}
        title="Session Expired"
        message="Your checkout session has expired. Prices may have changed. Please refresh the page to continue"
        buttonLabel="Refresh"
        buttonLoadingLabel="Refreshing..."
      />
    </div>
  );
}


const AgeRangeWarningModal = ({ isOpen, bounds, onUpdateSearch, onClose }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-dark/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
      <div className="bg-surface rounded-2xl shadow-card max-w-sm w-full p-6 border border-surface-border text-center">
        <div className="bg-primary-soft w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
          <Info className="w-8 h-8 text-primary" />
        </div>
        <h3 className="text-lg font-heading font-bold text-text mb-2">
          Driver Age Doesn't Match Search
        </h3>
        <p className="text-text-muted font-body text-sm mb-6 leading-relaxed">
          This search was made for drivers aged {bounds?.min}–{bounds?.max}.
          The date of birth you entered falls outside that range, so prices
          shown may not apply. Please start a new search with the correct
          driver age.
        </p>
        <button
          onClick={onUpdateSearch}
          className="w-full bg-primary hover:bg-primary-hover text-white font-heading font-bold py-3.5 rounded-xl transition-all shadow-card"
        >
          Start New Search
        </button>
        <button
          onClick={onClose}
          className="w-full py-2 mt-2 text-text-muted font-heading font-bold text-xs hover:text-text transition-colors"
        >
          Edit Date of Birth
        </button>
      </div>
    </div>
  );
};


const ChangeResidencyModal = ({
  isOpen,
  currentCountry,
  countries,
  onConfirm,
  onCancel,
  pendingResidency,
  setPendingResidency,
}) => {
  if (!isOpen) return null;

  const isUnchanged = pendingResidency === currentCountry;

  return (
    <div className="fixed inset-0 bg-dark/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
      {/* Modal Container with Premium Shadow */}
      <div className="bg-surface rounded-2xl shadow-card max-w-sm w-full p-6 animate-in fade-in zoom-in duration-200 border border-surface-border">
        <div className="flex flex-col items-center text-center">
          {/* Icon with Primary Soft Background */}
          <div className="bg-primary-soft p-3 rounded-full mb-3">
            <Info className="w-6 h-6 text-primary" />
          </div>

          <h3 className="text-lg tracking-wide font-heading font-bold text-text mb-1">
            Update Residency?
          </h3>
          <p className="text-text-muted font-body mb-5 text-xs leading-relaxed px-2">
            Are you sure you want to update your residency? This will update
            your search as{" "}
            <span className="text-text font-semibold">
              prices and availability may change.
            </span>
          </p>
        </div>

        <div className="space-y-4 font-body">
          <div className="flex flex-col gap-1.5">
            <label className="text-[10px] font-bold text-text-muted uppercase tracking-[0.1em] ml-1">
              Select Country
            </label>
            <select
              value={pendingResidency}
              onChange={(e) => setPendingResidency(e.target.value)}
              className="w-full border border-surface-border rounded-xl p-3 bg-surface text-sm font-medium text-text focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all appearance-none cursor-pointer"
              style={{ backgroundImage: 'url("data:image/svg+xml,...")' }} // Optional: add custom arrow
            >
              {countries.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-2 pt-1">
            <button
              onClick={onConfirm}
              disabled={isUnchanged}
              className={`w-full py-3.5 rounded-xl font-heading font-bold text-sm transition-all shadow-card 
                ${
                  isUnchanged
                    ? "bg-surface-border text-text-muted cursor-not-allowed shadow-none"
                    : "bg-primary text-white hover:bg-primary-hover hover:shadow-primary-glow active:scale-[0.98]"
                }`}
            >
              Confirm Update
            </button>
            <button
              onClick={onCancel}
              className="w-full py-2 text-text-muted font-heading font-bold text-xs hover:text-text transition-colors tracking-wide"
            >
              CANCEL
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};